import { inject, Injectable, makeStateKey, PLATFORM_ID, StateKey, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { combineLatest, defer, map, Observable, of, switchMap } from 'rxjs';
import { ProductService } from '../../../shared/services/product.service';
import { CategoryService } from '../../../shared/services/category.service';
import { Paginated, Product } from '../../../shared/models/product.model';
import { CategoryProductFilters, CategoryProductsResponse } from '../../../shared/models/category.model';
import { toUiProduct, toUiProductDetail } from '../../../shared/utils/product.utils';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import { CatalogParams, CatalogResponse, CategoryPathNode } from './catalog.models';
import {
  toBackendFilters,
  toCatalogCategory,
  toFilterSections,
  toUnifiedSort,
} from './catalog-mappers';

/**
 * Orquestador del catálogo: recibe los `CatalogParams` del componente
 * (filtros UI, búsqueda, sort UI, página) y decide a qué endpoint pegarle:
 *
 * - `/tienda` (sin categoría) → `ProductService.getProducts()` → `/avaliable-products`.
 * - `/tienda/:slug` → `CategoryService.getCategoryBySlug()` y luego
 *   `CategoryService.getCategoryProducts()` → `/category-web-products/{id}`.
 *
 * Además aplica los mappers (`toUnifiedSort`, `toBackendFilters`,
 * `toFilterSections`, `toCatalogCategory`) para traducir entre el shape
 * UI (camelCase, sort con guión) y el shape backend (snake_case, sort con _).
 */
@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly categoryService = inject(CategoryService);
  private readonly productService = inject(ProductService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  /**
   * Detalle de producto por slug SEO. Envuelve `ProductService.getProductBySlug`
   * y aplica `toUiProductDetail` para que la ficha y los related/FBT anidados
   * rindan con la misma forma que usan las cards del listing.
   *
   * NOTA: este método NO usa TransferState a propósito. El Product de detalle
   * (descripción HTML rica, images resueltas, related/FBT poblados) pesa cientos
   * de KB serializados, duplicando el HTML inicial de cada PDP. En SSR el fetch
   * se hace para alimentar el render; el cliente refetchea al hidratar.
   *
   * Errores: propaga `ProductNotFoundError` tal cual lo lanza `ProductService`
   * (reason: 'not_found' | 'disabled'). El componente decide el copy según reason.
   */
  getProductBySlug(slug: string): Observable<Product> {
    return this.productService.getProductBySlug(slug).pipe(map(toUiProductDetail));
  }

  /** Detalle por id — wrapper del `ProductService` con el mismo mapeo de detalle. */
  getProductById(id: number | string): Observable<Product> {
    return this.productService.getProduct(id).pipe(map(toUiProductDetail));
  }

  /**
   * Entrada única usada por `CatalogPageComponent`. Devuelve un `CatalogResponse`
   * normalizado independiente del endpoint que se haya usado por debajo.
   *
   * Usa `TransferState` para propagar el listado del render server al cliente y
   * evitar el re-fetch tras la hidratación (el "flash" data → skeleton → data al
   * entrar directo por URL a `/tienda`, `/tienda/:slug` o `/buscar`). Como estas
   * rutas son `RenderMode.Server` (render por request), sólo se serializa LA
   * vista actual (un listado ≤24 productos + facetas + categoryPath), no todas
   * las combinaciones de página/filtro — el coste es un listado por carga, que
   * es exactamente el objetivo. En un HIT se hace short-circuit antes del
   * `combineLatest([getCategoryBySlug, getCategoryPathBySlug])`.
   *
   * La clave se deriva de los `CatalogParams` EFECTIVOS (ver `catalogStateKey`),
   * no de la URL, para garantizar correctitud: si por preferencias de
   * localStorage los params del cliente difieren de los del server (que no tiene
   * localStorage), simplemente NO hay hit y cae al fetch normal (comportamiento
   * actual); nunca se sirve un listado que no corresponde a los controles
   * visibles. Ver la nota de `catalog-page.component.ts` sobre esa divergencia.
   */
  getCatalogData(params: CatalogParams): Observable<CatalogResponse> {
    // `defer` para que el pipeline (resolución de categoría + productos) sólo se
    // construya/dispare en un MISS; en un HIT servimos el snapshot sin tocarlo.
    return withTransferState(
      this.transferState,
      this.isBrowser,
      catalogStateKey(params),
      defer(() =>
        params.category ? this.loadCategoryCatalog(params) : this.loadGeneralCatalog(params),
      ),
    );
  }

  // ── Sin categoría: /tienda ─────────────────────────────────────────────────

  /**
   * Catálogo general. `avaliable-products` acepta el mismo sort unificado que
   * `/category-web-products` (incluyendo `popular` y `newest`), pero NO devuelve
   * facetas — por eso `availableFilters: []` y el componente oculta el sidebar.
   */
  private loadGeneralCatalog(params: CatalogParams): Observable<CatalogResponse> {
    return this.productService
      .getProducts({
        search: params.search || undefined,
        page: params.page,
        per_page: params.perPage,
        sort: toUnifiedSort(params.sort),
        on_discount: params.on_discount,
        // Mezcla productos regulares + precompras en el listado general de /tienda.
        // Las cards de precompra pintan CTA "Reservar" y abren el modal aparte.
        include_prepurchase: true,
      })
      .pipe(map((res) => this.toResponse(res, null)));
  }

  // ── Con categoría: /tienda/:slug ───────────────────────────────────────────

  /**
   * Catálogo de una categoría. Primero resuelve el slug al `Category` real
   * (vía el cache de `CategoryService`) y luego pide sus productos con
   * filtros + sort unificado. Si el slug no existe, devuelve un response
   * vacío para que el componente muestre su empty-state.
   */
  private loadCategoryCatalog(params: CatalogParams): Observable<CatalogResponse> {
    const slug = params.category!;
    return combineLatest([
      this.categoryService.getCategoryBySlug(slug),
      this.categoryService.getCategoryPathBySlug(slug),
    ]).pipe(
      switchMap(([category, path]) => {
        if (!category) {
          return of<CatalogResponse>({
            category: null,
            categoryPath: [],
            products: [],
            totalProducts: 0,
            pagination: { page: 1, perPage: params.perPage, totalPages: 1 },
            availableFilters: [],
          });
        }

        const backendFilters: CategoryProductFilters = {
          ...toBackendFilters(params.filters),
          page: params.page,
          per_page: params.perPage,
          sort: toUnifiedSort(params.sort),
          search: params.search || undefined,
          // Precompras mezcladas con productos regulares dentro de la categoría.
          // El backend recalcula las facetas sobre el set ampliado, así que los
          // filtros del sidebar coinciden con la lista que ve el usuario.
          include_prepurchase: true,
        };

        const pathNodes: CategoryPathNode[] = path.map((c) => ({
          name: c.name,
          slug: c.slug,
        }));

        return this.categoryService.getCategoryProducts(category.id, backendFilters).pipe(
          map((res) =>
            this.toResponseWithCategory(res, toCatalogCategory(category), pathNodes),
          ),
        );
      }),
    );
  }

  // ── Adapters a CatalogResponse ─────────────────────────────────────────────

  /** Paginated<Product> (avaliable-products) → CatalogResponse sin facetas. */
  private toResponse(
    res: Paginated<import('../../../shared/models/product.model').Product>,
    category: import('./catalog.models').CatalogCategory | null,
  ): CatalogResponse {
    return {
      category,
      categoryPath: [],
      products: (res.data ?? []).map(toUiProduct),
      totalProducts: res.total ?? 0,
      pagination: {
        page: res.current_page ?? 1,
        perPage: res.per_page ?? 24,
        totalPages: res.last_page ?? 1,
      },
      availableFilters: [],
    };
  }

  /** CategoryProductsResponse (con facetas) → CatalogResponse con filterSections. */
  private toResponseWithCategory(
    res: CategoryProductsResponse,
    category: import('./catalog.models').CatalogCategory,
    categoryPath: CategoryPathNode[],
  ): CatalogResponse {
    return {
      category,
      categoryPath,
      products: (res.data ?? []).map(toUiProduct),
      totalProducts: res.total ?? 0,
      pagination: {
        page: res.current_page ?? 1,
        perPage: res.per_page ?? 24,
        totalPages: res.last_page ?? 1,
      },
      availableFilters: toFilterSections(res.filters),
    };
  }
}

/**
 * Clave de TransferState de una vista concreta del catálogo, derivada de sus
 * `CatalogParams` EFECTIVOS (no de la URL). Ver el JSDoc de `getCatalogData`.
 */
function catalogStateKey(params: CatalogParams): StateKey<CatalogResponse> {
  return makeStateKey<CatalogResponse>(`perron.catalog.${serializeCatalogParams(params)}`);
}

/**
 * Serialización canónica y determinista de los `CatalogParams` (idéntica en
 * server y cliente para los mismos params):
 * - claves top-level en orden fijo,
 * - `filters` con claves ordenadas alfabéticamente y valores-array ordenados,
 * - vacíos normalizados con el MISMO criterio que `toBackendFilters`
 *   (`null`/`undefined`/`[]`/`''`/`'all'` = sin filtro) para no fragmentar hits
 *   ni colisionar hacia una request distinta.
 *
 * `viewMode` NO entra: es presentacional, no cambia la request al backend.
 *
 * Exportada para poder testear su determinismo de forma directa.
 */
export function serializeCatalogParams(params: CatalogParams): string {
  const filters = params.filters ?? {};
  const canonicalFilters: Record<string, string | string[]> = {};

  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value == null) continue;
    if (Array.isArray(value)) {
      const cleaned = value.filter((v) => v !== '' && v !== 'all');
      if (cleaned.length === 0) continue;
      canonicalFilters[key] = [...cleaned].sort();
    } else {
      if (value === '' || value === 'all') continue;
      canonicalFilters[key] = value;
    }
  }

  // Orden de claves fijo → JSON determinista.
  return JSON.stringify({
    category: params.category ?? null,
    search: params.search?.trim() ? params.search.trim() : '',
    page: params.page ?? 1,
    perPage: params.perPage ?? 24,
    sort: params.sort ?? 'newest',
    on_discount: params.on_discount === true,
    filters: canonicalFilters,
  });
}
